import { useMemo } from 'react';

import type { ShareChannel, ShareConfig } from '../shared/types';

import type { ShareAction } from './actions';
import { useShareActionsCore } from './actions';
import type { ShareLink, ShareUrlInfo } from './types';
import { useNativeShareFilteredLinks } from './useNativeShareFilteredLinks';

// Mirrors the GraphQL `ShareLink` type — see actions/types.ts.
export type { ShareLink };

export type ShareActionsOptions<CH extends ShareChannel = ShareChannel> = {
	config?: ShareConfig;
	trackingEventName: string;
	getContent: (channel: CH, type: 'title' | 'body' | 'image', context: { url: string }) => string;
	getTrackingData: (channel: CH) => Readonly<Record<string, boolean | string | number | undefined>>;
	onSuccess?: (channel: CH) => void | Promise<void>;
	onFailure?: (channel: CH) => void | Promise<void>;
};

/**
 * Builds share actions from server-provided `ShareLinks.links` (typically queried
 * from `Petition.shareLinks` on the middle-end). Long URLs, short URLs, and share
 * IDs all come pre-computed from the server; no client-side URL shortening or
 * shareId extraction happens here — it trusts the server's channel selection.
 * Channels gated by `useIsMobileNativeShareCapable` (`mobileNativeShare`,
 * `instagramStories`, `tiktok`) are filtered out client-side after hydration
 * when native share is unavailable; this may cause those buttons to disappear
 * briefly on devices that lack the Web Share API.
 *
 * When a link's `shortUrl` is null (e.g. the server-side shortener failed or didn't run), the
 * action falls back to the `longUrl`.
 */
export function useShareActions(
	links: readonly ShareLink[],
	{
		trackingEventName,
		getContent,
		getTrackingData,
		onSuccess,
		onFailure,
		config,
	}: ShareActionsOptions<ShareLink['channel']>,
): ReadonlyArray<ShareAction<ShareLink['channel']>> {
	const configWithDefaults = useMemo(() => config || {}, [config]);
	const filteredLinks = useNativeShareFilteredLinks(links);

	const byChannel = useMemo(() => new Map(filteredLinks.map((link) => [link.channel, link])), [filteredLinks]);
	const channels = useMemo(() => filteredLinks.map((link) => link.channel), [filteredLinks]);

	const getUrl = useMemo(
		() => (channel: ShareLink['channel']) => {
			const link = byChannel.get(channel);
			if (!link) throw new Error(`useShareActions: no link for channel "${channel}"`);
			return link.longUrl;
		},
		[byChannel],
	);

	// TODO when legacy logic is removed, we can simplify this to just return `link.shortUrl`
	// and handle the undefined case (shortening failed or wasn't performed) in the getShareActionConfig function
	const getShortUrlInfo = useMemo(
		() =>
			(channel: ShareLink['channel']): ShareUrlInfo => {
				const link = byChannel.get(channel);
				if (!link) throw new Error(`useShareActions: no link for channel "${channel}"`);
				return {
					url: link.shortUrl ?? link.longUrl,
					trackingProperties: {
						generated_share_id: link.shareId ?? '',
						generated_share_id_source: link.shareId ? 'shortener' : 'server_shortener_failure',
					},
				};
			},
		[byChannel],
	);

	return useShareActionsCore<ShareLink['channel']>(channels, {
		getUrl,
		getShortUrlInfo,
		getTrackingData,
		getContent,
		trackingEventName,
		onSuccess,
		onFailure,
		config: configWithDefaults,
	});
}

export type { ShareAction };
